/**
 * ServiceAccountsPage Component
 *
 * Main page for managing service accounts with search, filtering, and CRUD operations.
 * Service accounts are special users used for app job execution.
 */

import React, { useState, useMemo } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Alert } from '../../../components/ui/Alert';
import { ServiceAccountTable } from '../components/ServiceAccountTable';
import { CreateServiceAccountModal } from '../components/CreateServiceAccountModal';
import { EditServiceAccountModal } from '../components/EditServiceAccountModal';
import { ServiceAccountDetailsModal } from '../components/ServiceAccountDetailsModal';
import { useServiceAccounts } from '../hooks/useServiceAccounts';
import { useDeleteServiceAccount } from '../hooks/useDeleteServiceAccount';
import { useAuth } from '../../../contexts/AuthContext';
import { hasPermission } from '../../../lib/utils/permissions';
import { useLayoutProps } from '../../../hooks/useLayoutProps';
import { useConfirm } from '../../../hooks/useConfirm';
import type { ServiceAccountSanitized, ServiceAccountStatus } from '../../../types/service-account.types';
import { getErrorMessage } from '../../../lib/utils/errors';

export const ServiceAccountsPage: React.FC = () => {
  const { tenant, activeRole } = useAuth();
  const layoutProps = useLayoutProps();
  const { confirm, ConfirmDialog } = useConfirm();

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [selectedServiceAccount, setSelectedServiceAccount] = useState<ServiceAccountSanitized | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  const tenantId = tenant?.tenantId || '';

  // Permission checks - using iam:user permissions for service accounts
  const canCreate = hasPermission(activeRole?.permissions, 'iam:user:create');
  const canUpdate = hasPermission(activeRole?.permissions, 'iam:user:update');
  const canDelete = hasPermission(activeRole?.permissions, 'iam:user:delete');

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch service accounts
  const {
    data: serviceAccounts,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useServiceAccounts(tenantId);

  const deleteServiceAccountMutation = useDeleteServiceAccount(tenantId);

  // Filter service accounts
  const filteredServiceAccounts = useMemo(() => {
    if (!serviceAccounts) return [];

    let filtered = [...serviceAccounts];

    // Search filter
    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase();
      filtered = filtered.filter(
        (sa) =>
          sa.serviceAccountName.toLowerCase().includes(query) ||
          sa.serviceAccountCode.toLowerCase().includes(query) ||
          (sa.description && sa.description.toLowerCase().includes(query))
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((sa) => sa.status === statusFilter);
    }

    // Sort by createdAt descending (newest first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA;
    });

    return filtered;
  }, [serviceAccounts, debouncedSearch, statusFilter]);

  // Event handlers
  const handleViewServiceAccount = (serviceAccount: ServiceAccountSanitized) => {
    setSelectedServiceAccount(serviceAccount);
    setShowDetailsModal(true);
  };

  const handleEditServiceAccount = (serviceAccount: ServiceAccountSanitized) => {
    setSelectedServiceAccount(serviceAccount);
    setShowEditModal(true);
  };

  const handleDeleteServiceAccount = async (serviceAccount: ServiceAccountSanitized) => {
    const confirmed = await confirm({
      title: 'Delete Service Account',
      message: `Are you sure you want to delete "${serviceAccount.serviceAccountName}" (${serviceAccount.serviceAccountCode})? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmVariant: 'danger',
    });

    if (confirmed) {
      try {
        await deleteServiceAccountMutation.mutateAsync(serviceAccount.serviceAccountId);
      } catch (error) {
        console.error('Failed to delete service account:', error);
      }
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Service Accounts</h1>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Manage service accounts for app job execution
            </p>
          </div>
          {canCreate && (
            <Button
              variant="primary"
              onClick={() => setShowCreateModal(true)}
            >
              Create Service Account
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Search by name, code, or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="sm:w-48">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </Select>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleRefresh}
            disabled={isFetching}
          >
            <ArrowPathIcon className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Error Alerts */}
        {error && (
          <Alert variant="error">
            Failed to load service accounts: {getErrorMessage(error)}
          </Alert>
        )}

        {deleteServiceAccountMutation.isError && (
          <Alert variant="error">
            Failed to delete service account: {getErrorMessage(deleteServiceAccountMutation.error)}
          </Alert>
        )}

        {/* Service Account Table */}
        <div className="card shadow-sm rounded-lg overflow-hidden">
          <ServiceAccountTable
            serviceAccounts={filteredServiceAccounts}
            isLoading={isLoading}
            onView={handleViewServiceAccount}
            onEdit={handleEditServiceAccount}
            onDelete={handleDeleteServiceAccount}
            canEdit={canUpdate}
            canDelete={canDelete}
          />
        </div>

        {/* Stats */}
        {!isLoading && (
          <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Showing {filteredServiceAccounts.length} of {serviceAccounts?.length || 0} service accounts
          </div>
        )}

        {/* Modals */}
        <CreateServiceAccountModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          tenantId={tenantId}
        />

        <ServiceAccountDetailsModal
          serviceAccount={selectedServiceAccount}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedServiceAccount(null);
          }}
        />

        <EditServiceAccountModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedServiceAccount(null);
          }}
          tenantId={tenantId}
          serviceAccount={selectedServiceAccount}
        />

        {/* Confirmation Dialog */}
        {ConfirmDialog}
      </div>
    </DashboardLayout>
  );
};

export default ServiceAccountsPage;
